import { Button, Input, requiredRule, ValidatedInput } from '@finbeat/react-toolkit';
import useChecksTabHelper from 'components/Workplaces/Order/OrderInfo/Additions/useChecksTabHelper';
import 'components/Workplaces/Order/OrderInfo/OrderInfo.scss';
import { Dictionary } from 'lodash';
import { OrderChecksInfo, OrderScoring, OrderScoringHQueueParam } from 'models/api/order';
import { useOrderInfoContext } from 'providers/OrderInfoProvider';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
    Card,
    CardBody,
    CardHeader,
    CardTitle,
    Col,
    Container,
    ListGroup,
    ListGroupItem,
    Row,
} from 'reactstrap';
import { docMask } from 'utils/masks/docMasks';

const ChecksTab = ({
    checksInfoQueryFn,
    isEditable,
    isOpen,
    orderId,
    clientId,
}: {
    checksInfoQueryFn: () => Promise<OrderChecksInfo>;
    isEditable: boolean;
    isOpen: boolean;
    orderId: number;
    clientId: number;
}) => {
    const className = 'order-info-checks';
    const { t } = useTranslation();

    const { scoring, setScoring, submitOrderMutation } = useOrderInfoContext();
    const getHqueuesCallback = useCallback(
        (hqueues: Dictionary<Dictionary<OrderScoringHQueueParam>>) => ({
            hqueueTotal: hqueues['Hand_TotalCheck'],
            hqueueTransunion: hqueues['Hand_ExTransunion'],
            hqueueMisDatos: hqueues['Hand_ExMisDatos'],
        }),
        []
    );

    const { watch } = useFormContext<OrderScoring>();

    const {
        reasons,
        checksInfoFetching,
        dictsFetching,
        specialMarkMutation,
        hqueues,
        checksInfo,
        ScrField,
        InputOptions,
        isCanBlock,
        pvTotalCheckDict,
        reasonsForDeclinedDict,
        reasonsForApproveDict,
        checkTabDictInfo,
        isRepeatClient,
        isRepeatClientFetching,
    } = useChecksTabHelper({
        isOpen,
        checksInfoQueryFn,
        orderId,
        clientId,
        getHqueuesCallback,
    });

    const checkResultValue = watch('totalCheck.checkResult');
    const approveReasonValue = watch('totalCheck.approveReason');
    const rejectReasonValue = watch('totalCheck.rejectReason');

    const reasonsForDeclinedLvl2Dict = useMemo(
        () =>
            (checkTabDictInfo &&
                rejectReasonValue &&
                checkTabDictInfo['ReasonsForDeclined' + rejectReasonValue]) ||
            [],
        [checkTabDictInfo, rejectReasonValue]
    );

    const reasonsForApproveLvl2Dict = useMemo(
        () =>
            (checkTabDictInfo &&
                approveReasonValue &&
                checkTabDictInfo['ReasonsForApprove' + approveReasonValue]) ||
            [],
        [checkTabDictInfo, approveReasonValue]
    );

    return (
        <Container className={className}>
            <ListGroup flush>
                <ListGroupItem>
                    <Col>
                        <Row>
                            <Col className={`${className}__special-marked`}>
                                <Input
                                    type="checkbox"
                                    label={t('BackOfficeSpecialMark')}
                                    checked={scoring?.specialMark}
                                    className="mb-2"
                                    labelClassName="text-danger"
                                    onChange={(e) =>
                                        setScoring((prev) =>
                                            prev
                                                ? {
                                                      ...prev,
                                                      specialMark: e.target.checked,
                                                  }
                                                : prev
                                        )
                                    }
                                    disabled={checksInfoFetching || !isEditable}
                                    loading={checksInfoFetching}
                                />
                            </Col>
                        </Row>
                        {scoring?.specialMark && !checksInfoFetching && (
                            <Row>
                                <Input
                                    isFloating={false}
                                    type="textarea"
                                    value={scoring?.specialMarkComment ?? ''}
                                    onChange={(e) =>
                                        setScoring((prev) =>
                                            prev
                                                ? {
                                                      ...prev,
                                                      specialMarkComment: e.target.value,
                                                  }
                                                : prev
                                        )
                                    }
                                    loading={checksInfoFetching}
                                />
                            </Row>
                        )}
                        {isCanBlock && (
                            <Row className="mb-2">
                                <Col>
                                    <Button
                                        color="danger"
                                        outline
                                        type="submit"
                                        onClick={() => specialMarkMutation.mutate()}
                                        isPending={specialMarkMutation.isLoading}
                                    >
                                        <Trans i18nKey="BackOfficeSave" />
                                    </Button>
                                </Col>
                            </Row>
                        )}
                    </Col>
                </ListGroupItem>

                {(hqueues?.hqueueTransunion || hqueues?.hqueueMisDatos) && !checksInfoFetching && (
                    <ListGroupItem>
                        <Row>
                            <Col>
                                <Card className="h-100">
                                    <CardHeader>{t('BackOfficePaylaterData')}</CardHeader>
                                    <ListGroup flush>
                                        <ListGroupItem>
                                            <Input
                                                id="info-doc-num-field"
                                                value={checksInfo?.clientInfo.docNum ?? ''}
                                                maskProps={{
                                                    mask: docMask,
                                                }}
                                                label={t('BackOfficeDocNum')}
                                                loading={checksInfoFetching}
                                                disabled
                                            />
                                            <Input
                                                id="info-first-name-field"
                                                value={checksInfo?.clientInfo.firstName ?? ''}
                                                label={t('BackOfficeFirstName')}
                                                loading={checksInfoFetching}
                                                disabled
                                            />
                                            <Input
                                                id="info-middle-name-field"
                                                value={checksInfo?.clientInfo.middleName ?? ''}
                                                label={t('BackOfficeMiddleName')}
                                                loading={checksInfoFetching}
                                                disabled
                                            />
                                            <Input
                                                id="info-last-name-field"
                                                value={checksInfo?.clientInfo.lastName ?? ''}
                                                label={t('BackOfficeLastName')}
                                                loading={checksInfoFetching}
                                                disabled
                                            />
                                            <Input
                                                id="info-second-last-name-field"
                                                value={checksInfo?.clientInfo.secondLastName ?? ''}
                                                label={t('BackOfficeSecondLastName')}
                                                loading={checksInfoFetching}
                                                disabled
                                            />
                                        </ListGroupItem>
                                    </ListGroup>
                                </Card>
                            </Col>
                            <Col>
                                <Card className="h-100">
                                    <CardHeader>{t('BackOfficeExternalServiceData')}</CardHeader>
                                    <CardBody>
                                        {hqueues?.hqueueMisDatos && (
                                            <>
                                                <CardTitle>Verific</CardTitle>
                                                <ScrField
                                                    values={[
                                                        {
                                                            key: 'Ex_MD_DocType',
                                                            label: 'BackOfficeDocType',
                                                        },
                                                        {
                                                            key: 'Ex_MD_DocNumber',
                                                            label: 'BackOfficeDocNum',
                                                        },
                                                    ]}
                                                    hqueue={hqueues.hqueueMisDatos}
                                                />
                                                <ScrField
                                                    title="CreditVision"
                                                    values={[
                                                        {
                                                            key: 'Ex_MD_FirstName',
                                                            label: 'BackOfficeFirstName',
                                                        },
                                                        {
                                                            key: 'Ex_MD_LastName',
                                                            label: 'BackOfficeLastName',
                                                        },
                                                    ]}
                                                    hqueue={hqueues.hqueueMisDatos}
                                                />
                                            </>
                                        )}
                                        {hqueues?.hqueueTransunion && (
                                            <>
                                                <CardTitle>Transunion</CardTitle>
                                                <ScrField
                                                    values={[
                                                        {
                                                            key: 'Ex_Transunion_DocNumber',
                                                            label: 'BackOfficeDocNum',
                                                        },
                                                        {
                                                            key: 'Ex_Transunion_ClientName',
                                                            label: 'BackOfficeFirstName',
                                                        },
                                                    ]}
                                                    hqueue={hqueues.hqueueTransunion}
                                                />
                                                <ScrField
                                                    title="CreditVision"
                                                    values={[
                                                        {
                                                            key: 'Ex_Transunion_CV_DocNumber',
                                                            label: 'BackOfficeDocNum',
                                                        },
                                                        {
                                                            key: 'Ex_Transunion_CV_ClientName',
                                                            label: 'BackOfficeFirstName',
                                                        },
                                                    ]}
                                                    hqueue={hqueues.hqueueTransunion}
                                                />
                                                <ScrField
                                                    title="UbicaPlus"
                                                    values={[
                                                        {
                                                            key: 'Ex_Transunion_UBICA_DocNumber',
                                                            label: 'BackOfficeDocNum',
                                                        },
                                                        {
                                                            key: 'Ex_Transunion_UBICA_ClientName',
                                                            label: 'BackOfficeFirstName',
                                                        },
                                                    ]}
                                                    hqueue={hqueues.hqueueTransunion}
                                                />
                                            </>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </ListGroupItem>
                )}

                {reasons && Object.values(reasons).length > 0 && (
                    <ListGroupItem>
                        <div className="alert alert-danger" role="alert">
                            <ul>
                                {Object.values(reasons).map((x) => (
                                    <li key={x[0]}>{x[0]}</li>
                                ))}
                            </ul>
                        </div>
                    </ListGroupItem>
                )}
                {hqueues?.hqueueTotal && !checksInfoFetching && !isRepeatClientFetching && (
                    <ListGroupItem>
                        <Card>
                            <CardHeader>{t('BackOfficeFinalDecision')}</CardHeader>
                            <CardBody>
                                <Row>
                                    <Col>
                                        <ValidatedInput
                                            name="totalCheck.checkResult"
                                            type="select"
                                            loading={
                                                dictsFetching ||
                                                checksInfoFetching ||
                                                isRepeatClientFetching
                                            }
                                            options={
                                                <InputOptions
                                                    optionList={pvTotalCheckDict}
                                                    defaultValue="BackOfficeChooseValue"
                                                />
                                            }
                                            disabled={!isEditable || submitOrderMutation.isLoading}
                                            isFloating={false}
                                            rules={
                                                isEditable
                                                    ? {
                                                          required: requiredRule,
                                                      }
                                                    : undefined
                                            }
                                        />
                                    </Col>
                                    {checkResultValue === 'Decline' && (
                                        <Col>
                                            <ValidatedInput
                                                name="totalCheck.rejectReason"
                                                type="select"
                                                loading={
                                                    dictsFetching ||
                                                    checksInfoFetching ||
                                                    isRepeatClientFetching
                                                }
                                                options={
                                                    <InputOptions
                                                        optionList={reasonsForDeclinedDict}
                                                        defaultValue="BackOfficeChooseReason"
                                                    />
                                                }
                                                disabled={
                                                    !isEditable ||
                                                    checkResultValue !== 'Decline' ||
                                                    submitOrderMutation.isLoading
                                                }
                                                isFloating={false}
                                                rules={
                                                    checkResultValue === 'Decline'
                                                        ? {
                                                              required: requiredRule,
                                                          }
                                                        : undefined
                                                }
                                            />
                                        </Col>
                                    )}
                                    {checkResultValue === 'Decline' && (
                                        <Col>
                                            <ValidatedInput
                                                name="totalCheck.rejectReasonLvl2"
                                                type="select"
                                                loading={
                                                    dictsFetching ||
                                                    checksInfoFetching ||
                                                    isRepeatClientFetching
                                                }
                                                options={
                                                    <InputOptions
                                                        optionList={reasonsForDeclinedLvl2Dict}
                                                        defaultValue="BackOfficeChooseReason"
                                                    />
                                                }
                                                disabled={
                                                    !isEditable ||
                                                    checkResultValue !== 'Decline' ||
                                                    !rejectReasonValue ||
                                                    submitOrderMutation.isLoading ||
                                                    reasonsForDeclinedLvl2Dict.length === 0
                                                }
                                                isFloating={false}
                                                rules={
                                                    checkResultValue === 'Decline' &&
                                                    reasonsForDeclinedLvl2Dict.length > 0
                                                        ? {
                                                              required: requiredRule,
                                                          }
                                                        : undefined
                                                }
                                            />
                                        </Col>
                                    )}
                                    {checkResultValue === 'Approve' && isRepeatClient && (
                                        <Col>
                                            <ValidatedInput
                                                name="totalCheck.approveReason"
                                                type="select"
                                                loading={
                                                    dictsFetching ||
                                                    checksInfoFetching ||
                                                    isRepeatClientFetching
                                                }
                                                options={
                                                    <InputOptions
                                                        optionList={reasonsForApproveDict}
                                                        defaultValue="BackOfficeChooseReason"
                                                    />
                                                }
                                                disabled={
                                                    !isEditable ||
                                                    checkResultValue !== 'Approve' ||
                                                    submitOrderMutation.isLoading
                                                }
                                                isFloating={false}
                                                rules={
                                                    checkResultValue === 'Approve'
                                                        ? {
                                                              required: requiredRule,
                                                          }
                                                        : undefined
                                                }
                                            />
                                        </Col>
                                    )}
                                    {checkResultValue === 'Approve' && isRepeatClient && (
                                        <Col>
                                            <ValidatedInput
                                                name="totalCheck.approveReasonLvl2"
                                                type="select"
                                                loading={
                                                    dictsFetching ||
                                                    checksInfoFetching ||
                                                    isRepeatClientFetching
                                                }
                                                options={
                                                    <InputOptions
                                                        optionList={reasonsForApproveLvl2Dict}
                                                        defaultValue="BackOfficeChooseReason"
                                                    />
                                                }
                                                disabled={
                                                    !isEditable ||
                                                    checkResultValue !== 'Approve' ||
                                                    !approveReasonValue ||
                                                    submitOrderMutation.isLoading ||
                                                    reasonsForApproveLvl2Dict.length === 0
                                                }
                                                isFloating={false}
                                                rules={
                                                    checkResultValue === 'Approve' &&
                                                    reasonsForApproveLvl2Dict.length > 0
                                                        ? {
                                                              required: requiredRule,
                                                          }
                                                        : undefined
                                                }
                                            />
                                        </Col>
                                    )}
                                </Row>
                                <Row>
                                    <Col>
                                        <ValidatedInput
                                            name="totalCheck.dsc"
                                            type="textarea"
                                            disabled={!isEditable || submitOrderMutation.isLoading}
                                            isFloating={false}
                                            loading={checksInfoFetching}
                                        />
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </ListGroupItem>
                )}
            </ListGroup>
        </Container>
    );
};

export default ChecksTab;
